import type { Immutable } from 'immer';
import { AssertNever, CloneDeepMutable } from 'pandora-common';
import { useMemo } from 'react';
import * as z from 'zod';
import { BrowserStorage } from '../browserStorage.ts';
import { useMediaQuery } from '../common/useMediaQuery.ts';
import { useObservable } from '../observable.ts';
import type { SettingDriver } from '../ui/components/settings/settingsInputs.tsx';

export const GraphicsUpscalingSettingSchema = z.enum(['smooth', 'crisp-edges', 'pixelated']);
export type GraphicsUpscalingSetting = z.infer<typeof GraphicsUpscalingSettingSchema>;

export const GraphicsSettingsSchema = z.object({
	// Effects
	effectBlinking: z.boolean(),
	smoothMovement: z.enum(['auto', 'enabled', 'disabled']),
	// Quality
	textureResolution: z.enum(['auto', '1', '0.5', '0.25']),
	renderResolution: z.number().int().min(0).max(100),
	upscaling: GraphicsUpscalingSettingSchema,
	/** Non-disabled alphamasking suppresses antialias. */
	alphamaskEngine: z.enum(['pixi', 'customShader', 'disabled']),
	/** Whether to tell browser we prefer antialiasing. Requires page reload. */
	antialias: z.boolean(),
});
export type GraphicsSettings = z.infer<typeof GraphicsSettingsSchema>;

export const GRAPHICS_SETTINGS_DEFAULT: Readonly<GraphicsSettings> = {
	// Effects
	effectBlinking: true,
	smoothMovement: 'auto',
	// Quality
	textureResolution: 'auto',
	renderResolution: 100,
	upscaling: 'smooth',
	alphamaskEngine: 'disabled',
	antialias: true,
} as const;

export const GraphicsSettingsStorage = BrowserStorage.create<Partial<Immutable<GraphicsSettings>>>('settings.graphics', {}, GraphicsSettingsSchema.partial());

export function useGraphicsSettings(): GraphicsSettings {
	const overrides = useObservable(GraphicsSettingsStorage);
	return useMemo(() => ({
		...GRAPHICS_SETTINGS_DEFAULT,
		...overrides,
	}), [overrides]);
}

export function SetGraphicsSettings(changes: Partial<GraphicsSettings>): void {
	GraphicsSettingsStorage.value = {
		...GraphicsSettingsStorage.value,
		...changes,
	};
}

export function GetGraphicsEffectiveAntialias(): boolean {
	const { antialias, alphamaskEngine } = ({
		...GRAPHICS_SETTINGS_DEFAULT,
		...GraphicsSettingsStorage.value,
	});
	return antialias && alphamaskEngine === 'disabled';
}

export function ResetGraphicsSettings(settings: readonly (keyof GraphicsSettings)[]): void {
	GraphicsSettingsStorage.produce((v) => {
		const newValue = CloneDeepMutable(v);
		for (const s of settings) {
			delete newValue[s];
		}
		return newValue;
	});
}

export function useGraphicsSettingDriver<const Setting extends keyof GraphicsSettings>(setting: Setting): SettingDriver<Immutable<GraphicsSettings>[Setting]> {
	const settings = useObservable(GraphicsSettingsStorage);
	const currentValue: Immutable<GraphicsSettings>[Setting] | undefined = settings[setting];

	return useMemo((): SettingDriver<Immutable<GraphicsSettings>[Setting]> => ({
		currentValue,
		defaultValue: GRAPHICS_SETTINGS_DEFAULT[setting],
		onChange(newValue) {
			SetGraphicsSettings({ [setting]: newValue });
		},
		onReset() {
			ResetGraphicsSettings([setting]);
		},
	}), [currentValue, setting]);
}

//#region Setting-specific helpers and values

// Smooth movement

/**
 * Tests whether graphics smooth movement should be enabled or not, if using "auto" option.
 * @returns If smooth movement should be enabled by default
 */
export function useGraphicsSmoothMovementAutoEnabled(): boolean {
	return !useMediaQuery('(prefers-reduced-motion)');
}

/**
 * Tests whether graphics smooth movement should be enabled or not, if using "auto" option.
 * If it shouldn't be, it returns a short reason why not.
 * @returns If smooth movement should be enabled by default, or reason why not
 */
export function useGraphicsSmoothMovementAutoEnabledExplain(): [true] | [false, reason: string] {
	const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion)');

	return useMemo((): ReturnType<typeof useGraphicsSmoothMovementAutoEnabledExplain> => {
		if (prefersReducedMotion)
			return [false, 'system settings prefer reduced motion'];

		return [true];
	}, [prefersReducedMotion]);
}

/**
 * Checks whether graphics smooth movement should be enabled or not,
 * checking user's settings and resolving "auto" value if necessary.
 * @returns If smooth movement should be enabled
 */
export function useGraphicsSmoothMovementEnabled(): boolean {
	// get storage value
	const currentSmoothMovementSetting = useGraphicsSettings().smoothMovement;
	const smoothMovementAutoSetting = useGraphicsSmoothMovementAutoEnabled();
	// if auto call useGraphicsSmoothMovementAutoEnabled or useGraphicsSmoothMovementAutoEnabledExplain
	switch (currentSmoothMovementSetting) {
		case 'auto':
			return smoothMovementAutoSetting;
		case 'enabled':
			return true;
		case 'disabled':
			return false;
	}
	AssertNever(currentSmoothMovementSetting);
}

// Texture resolution

export const GRAPHICS_TEXTURE_RESOLUTION_SCALE: Record<Exclude<GraphicsSettings['textureResolution'], 'auto'>, number> = {
	'1': 1,
	'0.5': 2,
	'0.25': 4,
};

//#endregion
